<template>
  <div class="pref-wrap">
    <div class="page-header">
      <div>
        <h1 class="page-title">訂閱偏好中心</h1>
        <p class="page-sub">管理訂閱者的郵件類型偏好設定</p>
      </div>
    </div>

    <!-- Stats summary -->
    <div v-if="stats" class="stats-row">
      <div class="stat-chip">
        <div class="sc-num">{{ stats.total }}</div>
        <div class="sc-label">總訂閱者</div>
      </div>
      <div class="stat-chip danger">
        <div class="sc-num">{{ stats.unsubscribed_all }}</div>
        <div class="sc-label">全部退訂</div>
      </div>
      <div v-for="(v, k) in stats.categories" :key="k" class="stat-chip indigo">
        <div class="sc-num">{{ v.count }}</div>
        <div class="sc-label">{{ v.label }}</div>
      </div>
    </div>

    <!-- Filter -->
    <div class="filter-row">
      <input v-model="search" class="input" style="max-width:260px;" placeholder="搜尋 Email…" @input="filterList">
      <button class="btn btn-secondary btn-sm" @click="load">重新整理</button>
    </div>

    <!-- Table -->
    <div class="pref-table-wrap">
      <table class="pref-table" v-if="!loading">
        <thead>
          <tr>
            <th>Email</th>
            <th>訂閱類別</th>
            <th>全部退訂</th>
            <th>更新時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filtered.length === 0">
            <td colspan="5" class="text-center py-6" style="color:var(--gray-400)">尚無資料</td>
          </tr>
          <tr v-for="row in filtered" :key="row.id">
            <td class="email-cell">{{ row.email }}</td>
            <td>
              <span v-if="row.unsubscribed_all" class="chip chip-red">全退訂</span>
              <template v-else>
                <span v-for="(on, k) in row.categories" :key="k"
                  :class="['chip', on ? 'chip-green' : 'chip-gray']">
                  {{ catLabel(k) }}
                </span>
              </template>
            </td>
            <td>
              <span :class="row.unsubscribed_all ? 'chip chip-red' : 'chip chip-green'">
                {{ row.unsubscribed_all ? '是' : '否' }}
              </span>
            </td>
            <td class="text-xs" style="color:var(--gray-500)">{{ fmtDate(row.updated_at) }}</td>
            <td>
              <button class="btn btn-secondary btn-xs" @click="copyLink(row)" title="複製偏好設定連結">
                🔗 複製連結
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else class="text-center py-12" style="color:var(--gray-400)">載入中…</div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import axios from 'axios'
import { ADMIN_API } from '../store/app.js'

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` })

const loading = ref(false)
const prefs = ref([])
const stats = ref(null)
const search = ref('')

const CAT_LABELS = {
  newsletter: '📰 電子報',
  promo: '🎁 促銷',
  product_update: '🔔 產品更新',
  transaction: '📧 交易通知',
}
const catLabel = k => CAT_LABELS[k] || k
const fmtDate = d => d ? new Date(d).toLocaleString('zh-TW', { dateStyle: 'short', timeStyle: 'short' }) : ''

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return q ? prefs.value.filter(r => r.email.toLowerCase().includes(q)) : prefs.value
})

onMounted(load)

async function load() {
  loading.value = true
  try {
    const [prefsRes, statsRes] = await Promise.all([
      axios.get(`${ADMIN_API}/ai/pref-center/`, { headers: authHeader() }),
      axios.get(`${ADMIN_API}/ai/pref-center/stats`, { headers: authHeader() }),
    ])
    prefs.value = prefsRes.data || []
    stats.value = statsRes.data
  } catch (e) {
    window.$toast?.error('載入失敗', e?.response?.data?.detail || e.message)
  } finally {
    loading.value = false
  }
}

async function copyLink(row) {
  try {
    const res = await axios.post(`${ADMIN_API}/ai/pref-center/token`,
      { email: row.email, domain_id: row.domain_id },
      { headers: authHeader() })
    const url = res.data.url
    await navigator.clipboard.writeText(url)
    window.$toast?.success('已複製', url)
  } catch (e) {
    window.$toast?.error('失敗', e?.response?.data?.detail || e.message)
  }
}

function filterList() { /* filtered is computed */ }
</script>

<style scoped>
.pref-wrap { height: 100%; overflow-y: auto; padding: 28px 32px; }
.page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 20px; }
.page-title { font-size: 1.35rem; font-weight: 700; }
.page-sub { color: var(--gray-500); font-size: .875rem; margin-top: 2px; }

.stats-row { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 20px; }
.stat-chip { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px 16px; min-width: 90px; text-align: center; }
.stat-chip.danger { background: #fef2f2; border-color: #fecaca; }
.stat-chip.indigo { background: #eef2ff; border-color: #c7d2fe; }
.sc-num { font-size: 1.4rem; font-weight: 800; color: #1e293b; }
.stat-chip.danger .sc-num { color: #dc2626; }
.stat-chip.indigo .sc-num { color: #4f46e5; }
.sc-label { font-size: .75rem; color: #64748b; margin-top: 2px; }

.filter-row { display: flex; gap: 8px; margin-bottom: 16px; align-items: center; }

.pref-table-wrap { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: auto; }
.pref-table { width: 100%; border-collapse: collapse; font-size: .875rem; }
.pref-table th { padding: 10px 14px; text-align: left; font-weight: 700; color: #64748b; font-size: .75rem; text-transform: uppercase; letter-spacing: .05em; border-bottom: 1px solid #e2e8f0; background: #f8fafc; }
.pref-table td { padding: 10px 14px; border-bottom: 1px solid #f1f5f9; }
.pref-table tr:last-child td { border-bottom: none; }
.email-cell { font-weight: 600; color: #1e293b; }

.chip { display: inline-block; font-size: .7rem; font-weight: 600; border-radius: 6px; padding: 2px 7px; margin: 2px; }
.chip-green { background: #f0fdf4; color: #166534; border: 1px solid #86efac; }
.chip-gray  { background: #f1f5f9; color: #64748b; border: 1px solid #cbd5e1; }
.chip-red   { background: #fef2f2; color: #dc2626; border: 1px solid #fca5a5; }

.btn-xs { padding: 3px 10px; font-size: .75rem; }
</style>
